import {dateToPersian} from "../../../system/utils";

// Sentences of the project dashboard, written from the structured signals the server sends (spec «منطق معماری at
// risk»: the app builds the explanation from data, never from a hard-coded scenario). Wording follows the spec: «در خطر»,
// «نیازمند توجه», «شواهد تشخیص» — never «Blocked», «متوقف شده» or a dependency chain.

export const RISK_STATUS = {
    normal: {label: "عادی", tone: "green", icon: "check_circle"},
    needs_attention: {label: "نیازمند توجه", tone: "amber", icon: "error"},
    at_risk: {label: "در خطر", tone: "red", icon: "warning"},
};
export const HEALTH_STATUS = {
    normal: {label: "در مسیر", tone: "green", icon: "verified", headline: "پروژه طبق برنامه پیش می‌رود"},
    needs_attention: {label: "نیازمند توجه", tone: "amber", icon: "error", headline: "پروژه نیازمند توجه است"},
    at_risk: {label: "در خطر", tone: "red", icon: "warning", headline: "پروژه در خطر است"},
};
export const SEVERITY = {high: "ریسک بالا", medium: "ریسک متوسط", low: "ریسک کم"};
export const TASK_STATUS = {todo: "برای انجام", doing: "در حال انجام", blocked: "در حال انجام", done: "انجام‌شده"};
export const MILESTONE_STATUS = {todo: "آینده", in_progress: "در حال انجام", done: "تمام‌شده"};
export const GOAL_STATUS = {"on-way": "در مسیر", "has-risks": "در معرض ریسک", "out-of-way": "خارج از مسیر", achieved: "به‌دست‌آمده", canceled: "لغو شده", archived: "بیخیالش شدیم"};

export const shortDate = (value) => (value ? dateToPersian(value, "D MMMM") : "");
export const fullDate = (value) => (value ? dateToPersian(value, "D MMMM YYYY") : "");

export function daysText(days) {
    if (days === 0) return "امروز";
    if (days === 1) return "فردا";
    if (days > 0) return `${days} روز دیگر`;
    if (days === -1) return "دیروز";
    return `${-days} روز پیش`;
}

// «دیروز ۱۵:۴۰», «امروز ۹:۱۰», otherwise the date
export function whenText(value, now = Date.now()) {
    if (!value) return "";
    const date = new Date(value);
    const clock = dateToPersian(date, "HH:mm");
    const day = (time) => dateToPersian(time, "YYYY/MM/DD");
    if (day(date) === day(now)) return `امروز ${clock}`;
    if (day(date) === day(now - 86400000)) return `دیروز ${clock}`;
    return `${shortDate(date)} ${clock}`;
}

export const hoursText = (minutes) => {
    const hours = Math.round((minutes / 60) * 10) / 10;
    return `${hours} ساعت`;
};

// one piece of evidence: a short title and the fact behind it
export function signalText(signal) {
    const data = signal?.data || {};
    switch (signal?.type) {
        case "overdue":
            return {title: "سررسید گذشته", text: `${data.days} روز از موعد کار گذشته است`, detail: `سررسید: ${shortDate(data.dueDate)} · وضعیت فعلی: ${TASK_STATUS[data.taskStatus] || TASK_STATUS.todo}`};
        case "long_overdue":
            return {title: "تأخیر طولانی", text: `بیش از یک هفته (${data.days} روز) از موعد گذشته است`};
        case "due_soon_not_started":
            return {title: "موعد نزدیک", text: `موعد کار ${daysText(data.days)} است و کار هنوز شروع نشده`, detail: `سررسید: ${shortDate(data.dueDate)}`};
        case "remaining_estimate":
            return {title: "کار باقی‌مانده زیاد", text: `${hoursText(data.remainingMinutes)} برآورد باقی‌مانده و ${data.daysLeft ? `${data.daysLeft} روز` : "کمتر از یک روز"} تا موعد`, detail: "با فرض ۸ ساعت کار در روز"};
        case "no_activity":
            return {title: "فعالیت ثبت‌نشده", text: `${data.days} روز است تغییر یا زمان تمرکزی برای این کار ثبت نشده`, detail: `آخرین فعالیت ثبت‌شده: ${whenText(data.lastActivityAt)}`};
        case "milestone_due_soon":
            return {
                title: "ارتباط با مایلستون نزدیک به سررسید",
                text: `این کار به مایلستون «${data.milestone}» متصل است`,
                detail: `مایلستون: ${data.percent}٪ تکمیل · ${data.days >= 0 ? `${data.days} روز تا سررسید` : `${-data.days} روز از سررسید گذشته`} · ${data.openTasks} کار باز (سررسید مایلستون = دیرترین موعد کارهای آن)`,
            };
        case "member_overload":
            return {title: "ظرفیت مسئول", text: data.person ? `ظرفیت ثبت‌شده ${data.person} ${data.load}٪ پر شده` : `ظرفیت ثبت‌شده ${data.load}٪ پر شده`};
        case "member_many_overdue":
            return {title: "کارهای عقب‌افتاده", text: `${data.count} کار این عضو از موعد گذشته است`};
        case "milestone_overdue":
            return {title: "سررسید مایلستون گذشته", text: `${data.days} روز از سررسید گذشته و ${data.openTasks} کار آن هنوز باز است`};
        case "milestone_behind":
            return {title: "پیشرفت کمتر از انتظار", text: `${data.percent}٪ تکمیل شده، در حالی که تا امروز حدود ${data.expected}٪ انتظار می‌رفت`, detail: `${data.days} روز تا سررسید`};
        case "milestone_many_open":
            return {title: "کارهای باز نزدیک سررسید", text: `${data.openTasks} کار باز و ${data.days} روز تا سررسید`};
        case "milestone_risky_tasks":
            return {title: "کار در خطر", text: `${data.count} کار این مایلستون در خطر است`};
        case "schedule_gap":
            return {title: "عقب‌تر از برنامه", text: `${data.gap}٪ عقب‌تر از برنامه`};
        case "project_overdue":
            return {title: "موعد پروژه گذشته", text: `${data.days} روز از موعد پروژه گذشته و ${data.openTasks} کار هنوز باز است`};
        case "risky_tasks":
            return {title: "کارهای در خطر", text: `${data.count} کار در خطر است`};
        case "risky_milestones":
            return {title: "مایلستون در خطر", text: `${data.count} مایلستون در خطر است`};
        default:
            return {title: "نشانه", text: ""};
    }
}

// the one-line reason under an attention item: the strongest two signals
export const signalsSummary = (signals) => (signals || [])
    .slice()
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 2)
    .map((signal) => signalText(signal).text)
    .filter(Boolean)
    .join("؛ ");

export function attentionTitle(item) {
    const status = RISK_STATUS[item?.risk?.status]?.label || "";
    switch (item?.entityType) {
        case "task":
            return `کار «${item.title}» ${status === "در خطر" ? "در خطر است" : "نیازمند توجه است"}`;
        case "milestone":
            return `مایلستون «${item.title}» ${status === "در خطر" ? "در خطر است" : "نیازمند توجه است"}`;
        case "project":
            return "پروژه از برنامه عقب است";
        case "member":
            return `کارهای ${item.title} نیازمند بررسی است`;
        default:
            return item?.title || "";
    }
}

// the summary paragraph of the top strip, only from figures that exist
export function healthSentence(dashboard) {
    const parts = [];
    const progress = dashboard?.progress;
    if (progress?.planned !== null && progress?.planned !== undefined) {
        parts.push(`پیشرفت واقعی ${progress.actualInScope}٪ است و طبق برنامه تا امروز باید ${progress.planned}٪ می‌بود.`);
    } else if (progress) {
        parts.push(`پیشرفت واقعی ${progress.actual}٪ است؛ برای مقایسه با برنامه، موعد کافی برای کارها ثبت نشده.`);
    }
    const tasksAtRisk = (dashboard?.health?.signals || []).find((signal) => signal.type === "risky_tasks");
    if (tasksAtRisk) parts.push(`${tasksAtRisk.data.count} کار در خطر تشخیص داده شده.`);
    const milestone = (dashboard?.milestones || []).find((row) => row.risk?.status !== "normal" && row.status !== "done");
    if (milestone) parts.push(`مایلستون «${milestone.title}» ${RISK_STATUS[milestone.risk.status].label === "در خطر" ? "در خطر است" : "نیازمند توجه است"}.`);
    if (!parts.length) parts.push("هنوز داده‌ای برای تحلیل این پروژه ثبت نشده است.");
    return parts.join(" ");
}

export const FORECAST_REASONS = {
    no_open_tasks: "کار بازی نمانده است",
    few_recent_done: "در ۴ هفته گذشته کمتر از ۳ کار تمام شده",
    few_due_dates: "بیشتر کارهای باز موعد ندارند",
};

export const READINESS_LABELS = {
    owner: "مالک پروژه",
    goal: "اتصال به هدف",
    due_date: "موعد پایان پروژه",
    tasks: "حداقل ۳ کار",
    milestone: "حداقل یک مایلستون",
};

export const progressMethodText = (method) => (method === "estimate" ? "بر اساس برآورد زمان کارها" : "هر کار سهم برابر");
